import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Gauge, ShieldCheck, CheckCircle2, AlertTriangle, Lock, MinusCircle } from "lucide-react";
import { Loading } from "@/pages/_shared";
import { useEntitlements, trackEvent } from "@/hooks/useEntitlements";
import { ProviderCard, StatePill } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import api from "@/lib/api";
import { toast } from "sonner";

const SCORE_KEYS = ["business_credit.experian", "business_credit.dnb", "business_credit.equifax", "business_credit.fico"];

const ACTION_ROUTE = {
  business_identity: "/business-identity", profile_completeness: "/business-identity",
  contact_web: "/business-identity", industry: "/business-identity", entity_standing: "/business-identity",
  entity_type: "/business-identity", annual_revenue: "/business-identity", employees: "/business-identity",
  time_in_business: "/business-identity", business_credit: "/business-credit/setup",
  active_tradelines: "/business-credit/setup", documents: "/documents",
};

const FILE_STATUS = { strong: "Established", complete: "Established", needs_attention: "Thin File", missing: "No File Found" };

export default function BusinessCreditHub() {
  const { ent, loading, reload } = useEntitlements();
  const [readiness, setReadiness] = useState(null);
  const [modal, setModal] = useState(null); // {feature, requiredPlan}
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/funding-readiness/full").then(({ data }) => setReadiness(data)).catch(() => setReadiness(null));
    trackEvent("business_credit_viewed");
  }, []);

  const openUpgrade = (feature) => {
    trackEvent("locked_feature_viewed", { feature: feature.key });
    setModal({ feature: feature.key, requiredPlan: feature.required_plan });
  };
  const waitlist = (feature) => {
    api.post("/waitlist", { email: "", feature: feature.key }).catch(() => {});
    toast.success("We'll notify you when this is available.");
  };

  const scoreCards = useMemo(
    () => SCORE_KEYS.map((k) => ent?.features?.[k]).filter(Boolean), [ent]);

  if (loading || !ent) return <Loading />;

  const factors = readiness?.factors || [];
  // Business File Action Plan = profile-oriented factors (not provider score cards).
  const planItems = factors.filter((f) => ["business_identity", "contact_web", "industry", "entity_standing",
    "annual_revenue", "time_in_business", "employees", "documents", "active_tradelines"].includes(f.id));

  return (
    <div className="animate-fade-up" data-testid="business-credit-hub">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Welcome to <span className="text-foil">Millionaire Mindsetters™</span></h1>
      <p className="mt-3 max-w-2xl text-[16px] text-slate-500">
        See how your business financial profile may be viewed and what steps you can take to strengthen your funding readiness.
      </p>

      {/* SECTION 1 — scores */}
      <section className="mt-10">
        <h2 className="font-head text-xl font-extrabold text-slate-900">1. Review your business credit scores</h2>
        <p className="mt-1 text-[14px] text-slate-500">View available business credit information and understand the factors that may affect your business profile.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {scoreCards.map((f) => (
            <ProviderCard key={f.key} feature={f} onUpgrade={openUpgrade} onWaitlist={waitlist}
              onConnect={() => toast.info("Connection will open once MMT enables this provider.")} />
          ))}
        </div>
      </section>

      {/* SECTION 2 — bureau file issues + action plan */}
      <section className="mt-12">
        <h2 className="font-head text-xl font-extrabold text-slate-900">2. Let's fix your business bureau file issues</h2>
        <p className="mt-1 text-[14px] text-slate-500">We'll help you understand which files are available, what may be missing, and what needs attention.</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {scoreCards.filter((f) => f.key !== "business_credit.fico").map((f) => {
            const rf = factors.find((x) => x.id === "business_credit");
            const fileStatus = f.state === "included" || f.state === "connected"
              ? (FILE_STATUS[rf?.status] || "No File Found") : null;
            return (
              <div key={f.key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`bureau-file-${f.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-head text-[14px] font-bold text-slate-900">{f.name}</div>
                  <StatePill state={f.state} labelOverride={fileStatus} />
                </div>
                <div className="mt-3 space-y-1.5 text-[12px]">
                  {["File status", "Active tradelines", "Last updated"].map((label) => (
                    <div key={label} className="flex justify-between border-b border-slate-100 pb-1 last:border-0">
                      <span className="text-slate-400">{label}</span>
                      <span className="font-mono text-slate-500">—</span>
                    </div>
                  ))}
                </div>
                {f.state === "upgrade_required" && (
                  <button onClick={() => openUpgrade(f)} data-testid={`bureau-upgrade-${f.key}`}
                    className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#b0862f] px-3 py-1.5 font-head text-[12px] font-bold text-white"><Lock size={12} /> Upgrade Membership</button>
                )}
              </div>
            );
          })}
        </div>

        {/* Business File Action Plan */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="business-file-action-plan">
          <div className="font-head text-[15px] font-bold text-slate-900">Business File Action Plan</div>
          <p className="mt-0.5 text-[13px] text-slate-500">Complete these to strengthen your business identity and file.</p>
          <div className="mt-4 divide-y divide-slate-100">
            {planItems.map((f) => {
              const done = ["strong", "complete"].includes(f.status);
              const Icon = done ? CheckCircle2 : f.status === "needs_attention" ? AlertTriangle : f.status === "upgrade_required" ? Lock : MinusCircle;
              const tone = done ? "text-emerald-600" : f.status === "needs_attention" ? "text-amber-600" : f.status === "upgrade_required" ? "text-[#b0862f]" : "text-slate-400";
              return (
                <div key={f.id} className="flex items-center gap-3 py-2.5" data-testid={`action-item-${f.id}`}>
                  <Icon size={17} className={`shrink-0 ${tone}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-slate-800">{f.name}</div>
                    <div className="truncate text-[12px] text-slate-400">{f.display_value}</div>
                  </div>
                  {!done && (f.status === "upgrade_required"
                    ? <button onClick={() => openUpgrade(f)} className="shrink-0 rounded-lg bg-[#b0862f] px-3 py-1.5 text-[12px] font-bold text-white">Upgrade</button>
                    : <button onClick={() => navigate(ACTION_ROUTE[f.id] || "/business-identity")} data-testid={`action-fix-${f.id}`}
                        className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Fix</button>)}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — funding readiness summary */}
      <section className="mt-12">
        <h2 className="font-head text-xl font-extrabold text-slate-900">3. Review your overall funding readiness</h2>
        <div className="card-spotlight mt-4 flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center" data-testid="readiness-summary-card">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0f172a] text-white"><Gauge size={26} /></span>
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Your MMT Funding Readiness</div>
              {readiness?.score?.available
                ? <div className="font-head text-2xl font-extrabold text-slate-900" data-testid="readiness-value">{readiness.score.value}<span className="text-[14px] text-slate-400">/100 · {readiness.score.tier}</span></div>
                : <div className="font-head text-[17px] font-bold text-slate-700" data-testid="readiness-not-available">Not Yet Available</div>}
              <div className="mt-0.5 text-[12px] text-slate-500">
                {readiness?.score?.available ? "Based on the information you've provided." : "Complete your profile and connect the required information to calculate it."}
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/funding-readiness")} data-testid="view-readiness-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#1a5fd0] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1550b8]">
            View full analysis <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <p className="mt-10 flex items-center justify-center gap-2 text-center text-[12px] text-slate-400">
        <ShieldCheck size={14} /> No estimated or sample numbers are shown. Data appears only when a source is connected and authorized.
      </p>

      {modal && (
        <UpgradeModal open onClose={() => { setModal(null); reload(); }}
          feature={modal.feature} requiredPlan={modal.requiredPlan} returnRoute="/business-credit" ent={ent} />
      )}
    </div>
  );
}
